import React, { Component } from "react";
import { Input } from "reactstrap";
import { toast } from "react-toastify";
import { getOutlet, createOutlet, updateOutlet, getBranchList } from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import { C, Panel, Btn, Field, Toggle, IconBtn, Thumb } from "../../components/Console/ConsoleUI";
import { canWrite, isBranchScopedAdmin, getUserBranchName, getSelectedBranchId } from "../../services/PermissionService";
import { OUTLET_KINDS, DAYS, RESERVATION_DEFAULTS, pastMidnight } from "./outletMeta";

/**
 * Outlet editor — a restaurant, bar or café at one branch, its opening hours
 * and, when it takes reservations, the rules its time slots are cut from.
 *
 * The server validates everything (controllers/outlet.js, normaliseOutlet);
 * the checks here catch the same things before the round trip, and a failed
 * save shows the server's message. Every field is sent, so emptying one
 * clears it. Images are links, as on the branch's storefront tab.
 */

const CSS = `
.ol-editor .form-control,.ol-editor .custom-select{height:44px;padding:0 13px;
  background:${C.surface};border:1px solid ${C.border};border-radius:12px;font-size:15px;
  color:${C.ink};box-shadow:none}
.ol-editor textarea.form-control{height:auto;min-height:84px;padding:11px 13px;font-size:14px;line-height:1.5}
.ol-editor .form-control:focus{background:#fff;border-color:${C.sand};box-shadow:none}
.ol-chip{padding:7px 13px;border-radius:17px;font-size:13px;font-family:inherit;cursor:pointer;
  background:#fff;border:1px solid ${C.border};color:${C.taupe};line-height:1.3}
.ol-chip.is-on{background:${C.brown};border-color:transparent;color:#fff}
.ol-chip:disabled{cursor:default;opacity:.6}
.ol-row{display:grid;grid-template-columns:120px 120px minmax(0,1fr) minmax(0,1fr);gap:12px;
  align-items:center;padding:8px 0;border-bottom:1px solid ${C.panel}}
.ol-row:last-child{border-bottom:0}
.ol-window{padding:14px;border:1px solid ${C.tint};border-radius:14px;background:${C.surface}}
`;

const Chip = ({ on, onClick, children, disabled }) => (
  <button type="button" className={`ol-chip${on ? " is-on" : ""}`} onClick={onClick} disabled={disabled}>
    {children}
  </button>
);

const HHMM = /^([01]?\d|2[0-3]):[0-5]\d$/;
const LINK = /^(https?:\/\/|\/|uploads\/)\S+$/;
const toggleIn = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
const numText = (v) => (v === null || v === undefined ? "" : String(v));

// Same bounds as the server; the labels say what each number means here.
const LIMITS = {
  slotMinutes: [5, 240, "Slot length"],
  sittingMinutes: [15, 600, "Table time per booking"],
  maxPartySize: [1, 200, "Largest party"],
  leadMinutes: [0, 10080, "Minimum notice"],
  maxDaysAhead: [1, 365, "Bookable days ahead"],
};

// A day is open (with times), closed, or not set. The server takes a partial
// week: a day left out simply has no hours, so "not set" rows are not sent.
const emptyHours = () => DAYS.map((d) => ({ day: d.value, status: "open", openTime: "", closeTime: "" }));
const emptyWindow = () => ({ name: "", days: [], from: "19:00", to: "22:00" });

class OutletForm extends Component {
  constructor(props) {
    super(props);
    const id = props.match && props.match.params && props.match.params.id;
    this.state = {
      outletId: id || null,
      isLoading: !!id,
      saving: false,
      branches: [],
      name: "",
      kind: "restaurant",
      branchId: isBranchScopedAdmin() ? "" : getSelectedBranchId() || "",
      cuisineLabel: "",
      description: "",
      sortOrder: "0",
      active: true,
      image: "",
      images: [],
      imageDraft: "",
      // Off = "hours not set": the app then shows no open/closed badge.
      hoursEnabled: false,
      hours: emptyHours(),
      acceptsReservations: false,
      windows: [],
      slotMinutes: String(RESERVATION_DEFAULTS.slotMinutes),
      sittingMinutes: String(RESERVATION_DEFAULTS.sittingMinutes),
      maxPartySize: String(RESERVATION_DEFAULTS.maxPartySize),
      maxCoversPerSlot: "",
      leadMinutes: String(RESERVATION_DEFAULTS.leadMinutes),
      maxDaysAhead: String(RESERVATION_DEFAULTS.maxDaysAhead),
      seatingAreas: [],
      areaDraft: "",
    };
  }

  componentDidMount() {
    if (!isBranchScopedAdmin()) {
      getBranchList().then((res) => {
        if (res && res.success && Array.isArray(res.data)) this.setState({ branches: res.data });
      });
    }
    if (this.state.outletId) this.loadOutlet();
  }

  loadOutlet = () => {
    getOutlet(this.state.outletId).then((res) => {
      if (!res || !res.success || !res.data) {
        toast.error((res && res.message) || "Outlet not found");
        this.props.history.push("/outlets");
        return;
      }
      const o = res.data;
      const r = o.reservation || {};
      const byDay = new Map((o.operatingHours || []).map((h) => [h.day, h]));
      this.setState({
        isLoading: false,
        name: o.name || "",
        kind: o.kind || "restaurant",
        branchId: o.branchId ? String(o.branchId._id || o.branchId) : "",
        cuisineLabel: o.cuisineLabel || "",
        description: o.description || "",
        sortOrder: String(o.sortOrder || 0),
        active: o.active !== false,
        image: o.image || "",
        images: o.images || [],
        hoursEnabled: (o.operatingHours || []).length > 0,
        hours: DAYS.map((d) => {
          const h = byDay.get(d.value);
          const status = !h ? "unset" : h.isClosed ? "closed" : "open";
          return { day: d.value, status, openTime: (h && h.openTime) || "", closeTime: (h && h.closeTime) || "" };
        }),
        acceptsReservations: !!o.acceptsReservations,
        windows: (r.windows || []).map((w) => ({ name: w.name || "", days: w.days || [], from: w.from || "", to: w.to || "" })),
        slotMinutes: numText(r.slotMinutes != null ? r.slotMinutes : RESERVATION_DEFAULTS.slotMinutes),
        sittingMinutes: numText(r.sittingMinutes != null ? r.sittingMinutes : RESERVATION_DEFAULTS.sittingMinutes),
        maxPartySize: numText(r.maxPartySize != null ? r.maxPartySize : RESERVATION_DEFAULTS.maxPartySize),
        maxCoversPerSlot: numText(r.maxCoversPerSlot),
        leadMinutes: numText(r.leadMinutes != null ? r.leadMinutes : RESERVATION_DEFAULTS.leadMinutes),
        maxDaysAhead: numText(r.maxDaysAhead != null ? r.maxDaysAhead : RESERVATION_DEFAULTS.maxDaysAhead),
        seatingAreas: r.seatingAreas || [],
      });
    });
  };

  set = (patch) => this.setState(patch);

  setHour = (i, patch) => this.setState((s) => ({ hours: s.hours.map((h, j) => (j === i ? { ...h, ...patch } : h)) }));

  setWindow = (i, patch) => this.setState((s) => ({ windows: s.windows.map((w, j) => (j === i ? { ...w, ...patch } : w)) }));

  addImage = () => {
    const link = this.state.imageDraft.trim();
    if (!link) return;
    if (!LINK.test(link)) return toast.error("Image links must start with http(s)://, / or uploads/");
    if (this.state.images.includes(link)) return toast.error("That image is already in the gallery");
    this.setState((s) => ({ images: [...s.images, link], imageDraft: "" }));
  };

  addArea = () => {
    const area = this.state.areaDraft.trim();
    if (!area) return;
    if (this.state.seatingAreas.some((a) => a.toLowerCase() === area.toLowerCase())) return this.set({ areaDraft: "" });
    if (this.state.seatingAreas.length >= 10) return toast.error("Up to 10 seating areas");
    this.setState((s) => ({ seatingAreas: [...s.seatingAreas, area], areaDraft: "" }));
  };

  validate() {
    const s = this.state;
    if (!s.name.trim()) return "Outlet name is required";
    if (!isBranchScopedAdmin() && !s.branchId) return "Pick the outlet's branch";
    if (s.image.trim() && !LINK.test(s.image.trim())) return "The cover image link must start with http(s)://, / or uploads/";
    if (s.imageDraft.trim()) return "Add the gallery link you typed, or clear it";
    if (s.sortOrder.trim() && !Number.isInteger(Number(s.sortOrder))) return "Display order must be a whole number";
    if (s.hoursEnabled) {
      for (const h of s.hours) {
        if (h.status === "open" && (!HHMM.test(h.openTime) || !HHMM.test(h.closeTime))) {
          const label = DAYS.find((d) => d.value === h.day).label;
          return `Opening hours for ${label} need an open and a close time, or mark it closed`;
        }
      }
    }
    for (const w of s.windows) {
      if (!HHMM.test(w.from) || !HHMM.test(w.to)) return "Each reservation window needs from and to times";
    }
    if (s.acceptsReservations && !s.windows.length) return "Add at least one reservation window";
    for (const [k, [lo, hi, label]] of Object.entries(LIMITS)) {
      const n = Number(s[k]);
      if (s[k].trim() === "" || !Number.isInteger(n) || n < lo || n > hi) return `${label} must be a whole number from ${lo} to ${hi}`;
    }
    if (s.maxCoversPerSlot.trim()) {
      const n = Number(s.maxCoversPerSlot);
      if (!Number.isInteger(n) || n < 1 || n > 10000) return "Covers per slot must be from 1 to 10000, or empty for no limit";
    }
    if (s.areaDraft.trim()) return "Add the seating area you typed, or clear it";
    return null;
  }

  buildBody() {
    const s = this.state;
    const body = {
      name: s.name.trim(),
      kind: s.kind,
      cuisineLabel: s.cuisineLabel.trim(),
      description: s.description.trim(),
      sortOrder: Number(s.sortOrder) || 0,
      active: s.active,
      image: s.image.trim(),
      images: s.images,
      // [] = hours not set. A closed day carries no times; a "not set" day is left out.
      operatingHours: s.hoursEnabled
        ? s.hours
          .filter((h) => h.status !== "unset")
          .map((h) => {
            const closed = h.status === "closed";
            return { day: h.day, isClosed: closed, openTime: closed ? "" : h.openTime, closeTime: closed ? "" : h.closeTime };
          })
        : [],
      acceptsReservations: s.acceptsReservations,
      // Kept even while reservations are off, so switching them back on restores the rules.
      reservation: {
        windows: s.windows.map((w) => ({ name: w.name.trim(), days: w.days, from: w.from, to: w.to })),
        slotMinutes: Number(s.slotMinutes),
        sittingMinutes: Number(s.sittingMinutes),
        maxPartySize: Number(s.maxPartySize),
        maxCoversPerSlot: s.maxCoversPerSlot.trim() === "" ? "" : Number(s.maxCoversPerSlot),
        leadMinutes: Number(s.leadMinutes),
        maxDaysAhead: Number(s.maxDaysAhead),
        seatingAreas: s.seatingAreas,
      },
    };
    // A branch admin's outlets are pinned to their branch by the server.
    if (!isBranchScopedAdmin()) body.branchId = s.branchId;
    return body;
  }

  handleSubmit = () => {
    const error = this.validate();
    if (error) {
      toast.error(error);
      return;
    }
    const { outletId } = this.state;
    this.setState({ saving: true });
    const call = outletId ? updateOutlet(outletId, this.buildBody()) : createOutlet(this.buildBody());
    call.then((res) => {
      this.setState({ saving: false });
      if (res && res.success) {
        toast.success(res.message || "Outlet saved");
        this.props.history.push("/outlets");
      } else {
        toast.error((res && res.message) || "Could not save the outlet");
      }
    });
  };

  // --- sections --------------------------------------------------------------

  renderBasics(write) {
    const { name, kind, branchId, branches, cuisineLabel, description, sortOrder, active } = this.state;
    return (
      <Panel title="Outlet">
        <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 14 }}>
          <Field label="Name">
            <Input value={name} maxLength={80} onChange={(e) => this.set({ name: e.target.value })} placeholder="e.g. Rooftop Bar" />
          </Field>
          {isBranchScopedAdmin() ? (
            <Field label="Branch" hint="Outlets you add belong to your branch.">
              <Input value={getUserBranchName() || "Your branch"} disabled />
            </Field>
          ) : (
            <Field label="Branch">
              <Input type="select" value={branchId} onChange={(e) => this.set({ branchId: e.target.value })}>
                <option value="">Choose a branch</option>
                {branches.map((b) => <option key={b._id} value={b._id}>{b.name || b.code}</option>)}
              </Input>
            </Field>
          )}
        </div>
        <div style={{ marginTop: 16 }}>
          <Field label="Kind">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {OUTLET_KINDS.map((k) => (
                <Chip key={k.value} on={kind === k.value} disabled={!write} onClick={() => this.set({ kind: k.value })}>
                  {k.label}
                </Chip>
              ))}
            </div>
          </Field>
        </div>
        <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "minmax(0,1fr) 140px", gap: 14 }}>
          <Field label="Cuisine or style" hint="Shown under the name, e.g. “Congolese · Grill”.">
            <Input value={cuisineLabel} maxLength={60} onChange={(e) => this.set({ cuisineLabel: e.target.value })} />
          </Field>
          <Field label="Display order" hint="Lower comes first.">
            <Input type="number" step="1" value={sortOrder} onChange={(e) => this.set({ sortOrder: e.target.value })} />
          </Field>
        </div>
        <div style={{ marginTop: 16 }}>
          <Field label="Description">
            <Input type="textarea" value={description} onChange={(e) => this.set({ description: e.target.value })} />
          </Field>
        </div>
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <Toggle on={active} disabled={!write} onClick={() => this.set({ active: !active })} />
          <span style={{ fontSize: 14, color: C.ink }}>{active ? "Shown in the guest app" : "Hidden from the guest app"}</span>
        </div>
      </Panel>
    );
  }

  renderPhotos(write) {
    const { image, images, imageDraft } = this.state;
    return (
      <Panel title="Photos" subtitle="Links to images already hosted, as on the branch storefront.">
        <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "88px minmax(0,1fr)", gap: 14, alignItems: "end" }}>
          <Thumb src={image.trim() || null} icon="storefront" width={88} height={66} radius={12} />
          <Field label="Cover image link">
            <Input value={image} onChange={(e) => this.set({ image: e.target.value })} placeholder="https://…" />
          </Field>
        </div>
        <div style={{ marginTop: 16 }}>
          <Field label={`Gallery · ${images.length}/20`}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: images.length ? 12 : 0 }}>
              {images.map((src) => (
                <div key={src} style={{ position: "relative", width: 88 }}>
                  <Thumb src={src} width={88} height={66} radius={12} />
                  {write && (
                    <div style={{ position: "absolute", top: -8, right: -8 }}>
                      <IconBtn icon="close" title="Remove" tone="bad" onClick={() => this.set({ images: images.filter((x) => x !== src) })} />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {write && images.length < 20 && (
              <div style={{ display: "flex", gap: 10 }}>
                <Input
                  value={imageDraft}
                  onChange={(e) => this.set({ imageDraft: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); this.addImage(); } }}
                  placeholder="Paste an image link"
                />
                <Btn kind="quiet" icon="add" onClick={this.addImage}>Add</Btn>
              </div>
            )}
          </Field>
        </div>
      </Panel>
    );
  }

  renderHours(write) {
    const { hoursEnabled, hours } = this.state;
    return (
      <Panel title="Opening hours" subtitle="Branch time. A close at or before the open runs past midnight.">
        <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12 }}>
          <Toggle on={hoursEnabled} disabled={!write} onClick={() => this.set({ hoursEnabled: !hoursEnabled })} />
          <span style={{ fontSize: 14, color: C.ink }}>
            {hoursEnabled ? "Set per day" : "Not set — the app shows no open / closed badge"}
          </span>
        </div>
        {hoursEnabled && (
          <div style={{ marginTop: 12 }}>
            {hours.map((h, i) => (
              <div key={h.day} className="ol-row">
                <span style={{ fontSize: 14, color: C.ink }}>{DAYS[i].label}</span>
                <Input type="select" aria-label={`${DAYS[i].label} status`} value={h.status} disabled={!write}
                  onChange={(e) => this.setHour(i, { status: e.target.value })}>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="unset">Not set</option>
                </Input>
                {h.status !== "open" ? (
                  <span style={{ gridColumn: "3 / span 2", fontSize: 13, color: C.taupeLight }}>
                    {h.status === "closed" ? "Closed all day — no bookings" : "No hours shown for this day"}
                  </span>
                ) : (
                  <React.Fragment>
                    <Input type="time" aria-label={`${DAYS[i].label} opens`} value={h.openTime} onChange={(e) => this.setHour(i, { openTime: e.target.value })} />
                    <div>
                      <Input type="time" aria-label={`${DAYS[i].label} closes`} value={h.closeTime} onChange={(e) => this.setHour(i, { closeTime: e.target.value })} />
                      {pastMidnight(h.openTime, h.closeTime) && (
                        <div style={{ marginTop: 4, fontSize: 12, color: C.taupeLight }}>Closes the next morning</div>
                      )}
                    </div>
                  </React.Fragment>
                )}
              </div>
            ))}
            {write && (
              <div style={{ marginTop: 12 }}>
                <Btn
                  kind="quiet"
                  icon="content_copy"
                  onClick={() => {
                    const mon = hours[0];
                    this.set({ hours: hours.map((h) => ({ ...h, status: mon.status, openTime: mon.openTime, closeTime: mon.closeTime })) });
                  }}
                >
                  Copy Monday to every day
                </Btn>
              </div>
            )}
          </div>
        )}
      </Panel>
    );
  }

  renderReservations(write) {
    const s = this.state;
    const num = (key, label, hint, extra = {}) => (
      <Field label={label} hint={hint}>
        <Input type="number" step="1" value={s[key]} onChange={(e) => this.set({ [key]: e.target.value })} {...extra} />
      </Field>
    );
    return (
      <Panel
        title="Reservations"
        subtitle="Guests book a time slot. Slots start every slot length from “From”, the last one starting before “Until”, and skip closed days."
      >
        <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12 }}>
          <Toggle on={s.acceptsReservations} disabled={!write} onClick={() => this.set({ acceptsReservations: !s.acceptsReservations })} />
          <span style={{ fontSize: 14, color: C.ink }}>{s.acceptsReservations ? "Takes reservations" : "Walk-in only"}</span>
        </div>

        <div style={{ marginTop: 18, fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase", color: C.taupe }}>
          Booking windows
        </div>
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
          {s.windows.map((w, i) => (
            <div key={i} className="ol-window">
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 120px 120px 32px", gap: 12, alignItems: "end" }}>
                <Field label="Name (optional)">
                  <Input value={w.name} maxLength={40} onChange={(e) => this.setWindow(i, { name: e.target.value })} placeholder="e.g. Dinner" />
                </Field>
                <Field label="From">
                  <Input type="time" value={w.from} onChange={(e) => this.setWindow(i, { from: e.target.value })} />
                </Field>
                <Field label="Until">
                  <Input type="time" value={w.to} onChange={(e) => this.setWindow(i, { to: e.target.value })} />
                </Field>
                {write ? (
                  <IconBtn icon="delete" tone="bad" title="Remove window" onClick={() => this.set({ windows: s.windows.filter((_, j) => j !== i) })} />
                ) : <span />}
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {DAYS.map((d) => (
                  <Chip key={d.value} on={w.days.includes(d.value)} disabled={!write} onClick={() => this.setWindow(i, { days: toggleIn(w.days, d.value) })}>
                    {d.short}
                  </Chip>
                ))}
                <span style={{ fontSize: 12, color: C.taupeLight }}>
                  {w.days.length ? "" : "No day picked = every day. "}
                  {pastMidnight(w.from, w.to) ? "Runs past midnight." : ""}
                </span>
              </div>
            </div>
          ))}
          {write && (
            <div>
              <Btn kind="quiet" icon="add" onClick={() => this.set({ windows: [...s.windows, emptyWindow()] })}>
                Add a window
              </Btn>
            </div>
          )}
        </div>

        <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 14 }}>
          {num("slotMinutes", "Slot length (min)", "A start every N minutes.", { min: 5, max: 240 })}
          {num("sittingMinutes", "Table time (min)", "How long a booking holds its seats.", { min: 15, max: 600 })}
          {num("maxPartySize", "Largest party", "Guests per booking.", { min: 1, max: 200 })}
          <Field label="Covers per slot" hint="Seats booked around one time. Empty = no limit.">
            <Input type="number" step="1" min="1" value={s.maxCoversPerSlot} placeholder="No limit"
              onChange={(e) => this.set({ maxCoversPerSlot: e.target.value })} />
          </Field>
          {num("leadMinutes", "Minimum notice (min)", "0 = up to the slot itself.", { min: 0, max: 10080 })}
          {num("maxDaysAhead", "Bookable days ahead", null, { min: 1, max: 365 })}
        </div>

        <div style={{ marginTop: 18 }}>
          <Field label="Seating areas" hint="Optional — guests may pick one, e.g. Terrace, Indoor. Up to 10.">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: s.seatingAreas.length ? 10 : 0 }}>
              {s.seatingAreas.map((a) => (
                <Chip key={a} on disabled={!write} onClick={() => this.set({ seatingAreas: s.seatingAreas.filter((x) => x !== a) })}>
                  {a} ✕
                </Chip>
              ))}
            </div>
            {write && (
              <div style={{ display: "flex", gap: 10, maxWidth: 420 }}>
                <Input
                  value={s.areaDraft}
                  maxLength={40}
                  onChange={(e) => this.set({ areaDraft: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); this.addArea(); } }}
                  placeholder="Add an area"
                />
                <Btn kind="quiet" icon="add" onClick={this.addArea}>Add</Btn>
              </div>
            )}
          </Field>
        </div>
      </Panel>
    );
  }

  render() {
    const { outletId, isLoading, saving, name } = this.state;
    const write = canWrite();

    if (isLoading) {
      return (
        <ConsolePage title="Edit outlet" subtitle="Tables / Restaurants & bars">
          <div style={{ padding: "80px 0", textAlign: "center", color: C.taupeLight }}>Loading outlet…</div>
        </ConsolePage>
      );
    }

    return (
      <ConsolePage
        title={outletId ? `Edit · ${name}` : "New outlet"}
        subtitle="Tables / Restaurants & bars"
        actions={
          <React.Fragment>
            <Btn kind="quiet" icon="arrow_back" onClick={() => this.props.history.push("/outlets")}>
              Back to outlets
            </Btn>
            {write && (
              <Btn icon="check" onClick={this.handleSubmit} disabled={saving}>
                {saving ? "Saving…" : outletId ? "Save outlet" : "Create outlet"}
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />
        <div className="ol-editor" style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 980 }}>
          {this.renderBasics(write)}
          {this.renderPhotos(write)}
          {this.renderHours(write)}
          {this.renderReservations(write)}
        </div>
      </ConsolePage>
    );
  }
}

export default OutletForm;
